import { useEffect, useRef, useState } from 'react'

export function useMobileCarousel(track) {
  const [active, setActive] = useState(0)
  const [playback, setPlayback] = useState({ running: false, revision: 0 })
  const controller = useRef(null)
  useEffect(() => {
    const root = track.current
    if (!root?.children.length) return
    const interactionArea = root.parentElement
    const mobile = window.matchMedia('(max-width: 600px)')
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    const items = [...root.children]
    let visible = false, pressing = false, hovered = false, disposed = false, current = 0, timer
    function schedule() {
      clearTimeout(timer)
      if (disposed) return
      const running = mobile.matches && visible && !reduced.matches && !document.hidden && !pressing && !hovered && !interactionArea.contains(document.activeElement)
      setPlayback(previous => ({ running, revision: previous.revision + 1 }))
      if (running) {
        timer = setTimeout(() => goTo((current + 1) % items.length, true), 4500)
      }
    }
    function goTo(index, automatic = false) {
      current = index
      setActive(index)
      root.scrollTo({ left: items[index].offsetLeft - items[0].offsetLeft, behavior: reduced.matches || !automatic ? 'instant' : 'smooth' })
      schedule()
    }
    const scroll = () => {
      const first = items[0].offsetLeft
      current = items.reduce((nearest, item, index) => Math.abs(item.offsetLeft - first - root.scrollLeft) < Math.abs(items[nearest].offsetLeft - first - root.scrollLeft) ? index : nearest, 0)
      setActive(current)
      schedule()
    }
    const pause = () => { pressing = true; schedule() }
    const resume = () => { pressing = false; schedule() }
    const enter = event => { if (event.pointerType === 'mouse') { hovered = true; schedule() } }
    const leave = () => { hovered = false; schedule() }
    const focusOut = () => queueMicrotask(schedule)
    const sync = () => {
      if (!mobile.matches) { root.scrollLeft = 0; current = 0; setActive(0) }
      else goTo(current)
      schedule()
    }
    const hash = () => {
      let id
      try { id = decodeURIComponent(window.location.hash.slice(1)) } catch { return }
      const target = document.getElementById(id)
      const index = items.findIndex(item => item.contains(target))
      if (mobile.matches && index >= 0) goTo(index)
    }
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule() }, { threshold: .15 })
    observer.observe(root)
    controller.current = { goTo }
    root.addEventListener('scroll', scroll, { passive: true })
    interactionArea.addEventListener('pointerdown', pause)
    interactionArea.addEventListener('pointerenter', enter)
    interactionArea.addEventListener('pointerleave', leave)
    interactionArea.addEventListener('focusin', schedule)
    interactionArea.addEventListener('focusout', focusOut)
    window.addEventListener('pointerup', resume)
    window.addEventListener('pointercancel', resume)
    window.addEventListener('hashchange', hash)
    window.addEventListener('resize', sync)
    document.addEventListener('visibilitychange', schedule)
    mobile.addEventListener('change', sync)
    reduced.addEventListener('change', schedule)
    hash()
    return () => {
      disposed = true
      clearTimeout(timer)
      observer.disconnect()
      controller.current = null
      root.removeEventListener('scroll', scroll)
      interactionArea.removeEventListener('pointerdown', pause)
      interactionArea.removeEventListener('pointerenter', enter)
      interactionArea.removeEventListener('pointerleave', leave)
      interactionArea.removeEventListener('focusin', schedule)
      interactionArea.removeEventListener('focusout', focusOut)
      window.removeEventListener('pointerup', resume)
      window.removeEventListener('pointercancel', resume)
      window.removeEventListener('hashchange', hash)
      window.removeEventListener('resize', sync)
      document.removeEventListener('visibilitychange', schedule)
      mobile.removeEventListener('change', sync)
      reduced.removeEventListener('change', schedule)
    }
  }, [track])
  return { active, playback, goTo: index => controller.current?.goTo(index) }
}

export function CarouselIndicators({ carousel, labels, title }) {
  return <div className="mobile-carousel-indicators" role="group" aria-label={title} data-playing={carousel.playback.running}>
    {labels.map((label, index) => <button className="mobile-carousel-dot" key={index} type="button" aria-label={label} title={label} aria-current={carousel.active === index ? 'true' : undefined} onClick={() => carousel.goTo(index)}><span aria-hidden="true"><i key={carousel.playback.revision} /></span></button>)}
  </div>
}
